import * as R from 'ramda';
import { SetStateAction, useCallback, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useHandleAppException, {
  AppExceptionResponseData,
} from '../../../hooks/useHandleAppException';
import LangEnum from '../../../lang/LangEnum';
import api, { restApi } from '../../../services/api';
import { FederatedSession } from '../../../services/modules/sso/sso.types';
import {
  clearFederatedSessionFlag,
  consumeSsoRedirectPath,
} from '../../../services/modules/sso/sso.utils';
import useSharedEntitystore from '../../../stores/SharedEntities/sharedEntities.store';
import snackbarStore from '../../../stores/Snackbar.store';
import { dismissKeyboardAndResetScroll } from '../../../utils/viewport.utils';
import { Team } from '../../context/ApplicationContext/applicationContext.types';
import useAppEnvironment from '../../context/ApplicationContext/hooks/useAppEnvironment/useAppEnvironment';
import { StorageEnum } from '../../context/storage';
import { UserLogin } from '../../create-account/CreateContextLogin';
import { useTranslation } from '../../translation/TranslationContext';
import { User } from '../../user-admin/user.types';
import { UserMainStorage, useAuthContext } from '../AuthContext';
import {
  HandleLoginErrorResponse,
  HandleLoginResult,
  HandleLoginSuccessResponse,
  LoginApiResponse,
} from '../types/login.types';

export interface LoginCredentials {
  username?: string;
  password?: string;
}

interface UseAuthLoginReturn {
  doLogin: (credentials: LoginCredentials) => Promise<HandleLoginResult>;
  doFederatedLogin: (
    session: FederatedSession,
    loginBrandId?: string,
  ) => Promise<HandleLoginResult>;
  userInfo: UserLogin;
  setOpenTeamModal: React.Dispatch<React.SetStateAction<boolean>>;
  openTeamModal: boolean;
  handleSelectTeam: (team: Team) => Promise<void>;
  handleSelectPersonatedUser: (user: User) => Promise<void>;
  openUsersModal: boolean;
  setOpenUsersModal: React.Dispatch<React.SetStateAction<boolean>>;
  openActivationModal: boolean;
  setOpenActivationModal: React.Dispatch<SetStateAction<boolean>>;
}

const initialUserState: UserLogin = {
  user: {
    id: '',
    email: '',
    name: '',
    surname: '',
    ok: false,
    access_token: '',
  },
  teams: [],
};

export const useAuthLogin = (): UseAuthLoginReturn => {
  const { loginContext, handleUserLogin, redirectPath } = useAuthContext();
  const history = useHistory();
  const handleChangeSharedEntity = useSharedEntitystore((state) => state.handleChangeSharedEntity);
  const { translate } = useTranslation();
  const { handleMessage } = snackbarStore;
  const { getEnvironment } = useAppEnvironment();
  const { handleError } = useHandleAppException();

  const [userInfo, setUserInfo] = useState<UserLogin>(initialUserState);
  const [adminInfo, setAdminInfo] = useState<{ adminTeam: Team; token: string }>();
  const [openTeamModal, setOpenTeamModal] = useState(false);
  const [openUsersModal, setOpenUsersModal] = useState(false);
  const [openActivationModal, setOpenActivationModal] = useState(false);
  const loginBrandIdRef = useRef<string | undefined>(undefined);

  const wrongCredentialsMessage = translate(LangEnum.LOGIN_WORNG_CREDENCIALS);

  const loginContextFromEvents = loginContext?.slug.split('/')[0] === 'events';

  const resetSharedEntitiesRules = useCallback(() => {
    handleChangeSharedEntity(undefined);
    localStorage.removeItem('publicUser');
    localStorage.removeItem('publicSharedEntity');
    api.resetPublicId();
  }, [handleChangeSharedEntity]);

  const handleLogin = useCallback(
    async ({ username, password }: LoginCredentials): Promise<HandleLoginResult> => {
      const response: LoginApiResponse = await api.login({ username, password });

      if (!response.ok) {
        const errorData = response.data as unknown as Partial<AppExceptionResponseData>;
        const errorResponse: HandleLoginErrorResponse = {
          ok: false,
          message: handleError({
            ...errorData,
            message: wrongCredentialsMessage,
          } as AppExceptionResponseData),
        };
        return errorResponse;
      }

      const { data } = response;

      api.setToken(data.access_token ?? '');

      clearFederatedSessionFlag();

      resetSharedEntitiesRules();

      const successResponse: HandleLoginSuccessResponse = {
        ok: true,
        id: data.id,
        email: data.email,
        name: data.name,
        surname: data.surname,
        profileUrl: data.profileUrl,
        access_token: data.access_token,
        firstAccess: data.firstAccess,
      };
      return successResponse;
    },
    [resetSharedEntitiesRules, wrongCredentialsMessage, handleError],
  );
  const afterLogin = useCallback(
    (data: UserMainStorage) => {
      handleUserLogin(data);
      dismissKeyboardAndResetScroll();
      history.replace(consumeSsoRedirectPath() ?? redirectPath);
    },
    [handleUserLogin, history, redirectPath],
  );

  const acceptOrCreateInvite = useCallback(
    async (team: Team) => {
      const brandId = loginBrandIdRef.current ?? loginContext?.id;

      if (brandId && team.store && !loginContextFromEvents) {
        await restApi.post(`/brands/${brandId}/connection-or-invite`);
      }
    },
    [loginContext?.id, loginContextFromEvents],
  );

  const handleDirectLogin = useCallback(
    async (team: Team, token: string) => {
      const { id } = team;
      const appEnvironment = getEnvironment(team.role, team.brand?.id);

      api.setTeam(id);
      api.setAppEnvironment(appEnvironment);

      const data: UserMainStorage = {
        [StorageEnum.SESSION_ID]: token,
        [StorageEnum.TEAM_ID]: id,
        [StorageEnum.ADMIN_TEAM_ID]: adminInfo?.adminTeam.id ?? '',
        [StorageEnum.BRAND_ID]: '',
        [StorageEnum.APP_ENVIRONMENT]: appEnvironment,
      };

      await acceptOrCreateInvite(team);
      afterLogin(data);
    },
    [acceptOrCreateInvite, afterLogin, getEnvironment, adminInfo],
  );

  const handleSelectTeam = useCallback(
    async (team: Team) => {
      const appEnvironment = getEnvironment(team.role, team.brand?.id);

      api.setTeam(team.id);
      api.setAppEnvironment(appEnvironment);

      const data: UserMainStorage = {
        [StorageEnum.SESSION_ID]: userInfo.user?.access_token ?? '',
        [StorageEnum.TEAM_ID]: team.id,
        [StorageEnum.ADMIN_TEAM_ID]: adminInfo?.adminTeam.id ?? '',
        [StorageEnum.BRAND_ID]: '',
        [StorageEnum.APP_ENVIRONMENT]: appEnvironment,
      };

      await acceptOrCreateInvite(team);
      afterLogin(data);
    },
    [acceptOrCreateInvite, afterLogin, getEnvironment, adminInfo, userInfo],
  );

  const doPersonateLogin = useCallback(async (adminTeam: Team, token: string) => {
    api.setAdminTeam(adminTeam.id);
    setAdminInfo({ adminTeam, token });
    setOpenUsersModal(true);
  }, []);

  const handleSelectPersonatedUser = useCallback(
    async (user: User) => {
      setOpenUsersModal(false);

      const newUserInfo: UserLogin = {
        teams: user.teams,
        user: {
          id: user.id,
          email: user.email,
          name: user.name,
          surname: user.surname,
          ok: true,
          access_token: adminInfo?.token as string,
        },
      };

      setUserInfo(newUserInfo);

      if (newUserInfo.teams && R.length(newUserInfo.teams) === 1) {
        await handleDirectLogin(newUserInfo.teams[0], adminInfo?.token as string);
        return;
      }

      setOpenTeamModal(true);
    },
    [adminInfo, handleDirectLogin],
  );

  const showLoginSuccessMessage = useCallback(
    (userName?: string) => {
      handleMessage({
        type: 'success',
        message: translate(LangEnum.LOGIN_WELCOME_NAME, { name: userName }),
      });
    },
    [handleMessage, translate],
  );

  const processUserTeams = useCallback(
    async ({
      loginResponse,
      teams,
    }: {
      loginResponse: HandleLoginSuccessResponse;
      teams: Team[];
    }) => {
      setUserInfo({ user: loginResponse, teams });

      const adminTeam = teams.find(({ role }) => role === 'ADMIN');
      const hasSingleTeam = teams && teams.length === 1;
      const singleTeam = hasSingleTeam ? teams[0] : null;

      if (adminTeam) {
        await doPersonateLogin(adminTeam, loginResponse.access_token);
        return loginResponse;
      }

      if (singleTeam) {
        await handleDirectLogin(singleTeam, loginResponse.access_token);
        return loginResponse;
      }

      setOpenTeamModal(true);
      return loginResponse;
    },
    [doPersonateLogin, handleDirectLogin],
  );

  const completeAuthentication = useCallback(
    async (
      loginResponse: HandleLoginSuccessResponse,
      loginBrandId?: string,
    ): Promise<HandleLoginResult> => {
      const loginContextId = loginBrandId ?? loginContext?.id;
      loginBrandIdRef.current = loginContextId;

      const teamResponse = await api.getTeams(loginContextFromEvents ? undefined : loginContextId);

      if (!teamResponse.ok) {
        handleMessage({ type: 'error', message: teamResponse.data?.message });
        return { ok: false, message: teamResponse.problem };
      }

      showLoginSuccessMessage(loginResponse?.name);
      setUserInfo({
        user: loginResponse,
        teams: teamResponse.data?.data as Team[],
      });

      return processUserTeams({
        teams: teamResponse.data?.data as Team[],
        loginResponse,
      });
    },
    [
      handleMessage,
      loginContext?.id,
      loginContextFromEvents,
      processUserTeams,
      showLoginSuccessMessage,
    ],
  );

  const doLogin = useCallback(
    async (credentials: LoginCredentials): Promise<HandleLoginResult> => {
      const loginResponse = await handleLogin(credentials);
      if (!loginResponse.ok) {
        handleMessage({ type: 'error', message: loginResponse.message });
        return loginResponse;
      }

      if (loginResponse.firstAccess) {
        setOpenActivationModal(true);
        return loginResponse;
      }

      return completeAuthentication(loginResponse);
    },
    [handleLogin, handleMessage, completeAuthentication],
  );

  const doFederatedLogin = async (
    session: FederatedSession,
    loginBrandId?: string,
  ): Promise<HandleLoginResult> => {
    api.setToken(session.access_token);
    resetSharedEntitiesRules();

    const loginResponse: HandleLoginSuccessResponse = { ok: true, ...session };

    return completeAuthentication(loginResponse, loginBrandId);
  };

  return {
    doLogin,
    doFederatedLogin,
    userInfo,
    setOpenTeamModal,
    handleSelectTeam,
    handleSelectPersonatedUser,
    openTeamModal,
    openUsersModal,
    setOpenUsersModal,
    openActivationModal,
    setOpenActivationModal,
  };
};
